// StickyHeader + big-logo scroll behaviour.
// Extracted from the header section's {% javascript %} block because the local
// Shopify dev server truncates the merged compiled_assets/scripts.js (drops the
// trailing `);` of the last section wrapper), which left customElements.define
// for <sticky-header> unparsed → --header-height never set → top gap / jank.
// Loading as a standalone asset bypasses that broken pipeline.
(function () {
  if (!customElements.get('sticky-header')) {
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Scoped to this instance's own Shopify section wrapper, not a document-wide
        // lookup - header-alternate renders alongside the default header (hidden via
        // CSS, not removed from the DOM) on the pages it replaces it on, so a global
        // '.section-header' query would always resolve to the hidden default header
        // instead of whichever header is actually visible.
        this.header = this.closest('[id^="shopify-section-"]');
        this.headerIsAlwaysSticky =
          this.getAttribute('data-sticky-type') === 'always' ||
          this.getAttribute('data-sticky-type') === 'reduce-logo-size';
        this.isCollectionPage = this.getAttribute('data-is-collection') === 'true';
        this.isContentPage = this.getAttribute('data-is-page') === 'true';
        this.headerBounds = this.header ? this.header.getBoundingClientRect() : {};
        this.setHeaderHeight();
        // Reveal dependents (e.g. the page template sticky menu) the moment the
        // header is actually rendered and on screen — observed, not timed. An
        // IntersectionObserver fires only once the header has real layout and is
        // painted in the viewport, so the menu appears right after the header is
        // visible with no fixed delay (and no flash of the menu before it).
        this.revealReadyObserver = new IntersectionObserver((entries, obs) => {
          const entry = entries[0];
          if (entry && entry.isIntersecting && entry.target.offsetHeight > 0) {
            this.setHeaderHeight();
            document.documentElement.classList.add('header-ready');
            obs.disconnect();
            this.revealReadyObserver = null;
          }
        });
        this.revealReadyObserver.observe(this.header);
        this.syncAnnouncementPastClass(window.pageYOffset || document.documentElement.scrollTop);

        window.matchMedia('(max-width: 1023px)').addEventListener('change', this.setHeaderHeight.bind(this));
        this._menuDrawerDetails = this.querySelector('#Details-menu-drawer-container');
        if (this._menuDrawerDetails) {
          this._menuDrawerToggle = () => {
            requestAnimationFrame(() => this.setHeaderHeight());
          };
          this._menuDrawerDetails.addEventListener('toggle', this._menuDrawerToggle);
        }

        if (this.headerIsAlwaysSticky) {
          this.header.classList.add('shopify-section-header-sticky');
        }

        // On PDP mobile: make header sticky from the start so it stays at viewport top.
        // Without this, header scrolls out of view in normal flow before first animation.
        const isPdpMobile = this.getAttribute('data-is-pdp') === 'true' && window.innerWidth <= 1023;
        if (isPdpMobile) {
          this.header.classList.add('shopify-section-header-sticky');
        }

        // Seed with the actual scroll position so a browser scroll-restoration
        // on reload mid-page isn't read as a downward scroll from 0 (which would
        // hide the header even though the user isn't scrolling down).
        const initialScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        this.currentScrollTop = initialScrollTop;
        this.lastScrollTop = initialScrollTop;
        this.preventReveal = false;
        // On PDP mobile: keep header visible until parallax animation releases it.
        // product-parallax-mobile.js calls thawHeaderAfterDrag() which clears this.
        this.preventHide = isPdpMobile;
        this.predictiveSearch = this.querySelector('predictive-search');

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, false);

        this.updateHeaderVisibilityClass(window.pageYOffset || document.documentElement.scrollTop);

        this.createObserver();
        window.addEventListener('scroll', function () {

          const scrollPosition = window.scrollY;
          if (scrollPosition > 100) {
            // document.querySelector('sticky-header').classList.remove('transprent-header');
          } else {
            // document.querySelector('sticky-header').classList.add('transprent-header');
          }
        });
      }

      setHeaderHeight() {
        if (!this.header) return;
        // Scoped to this.header (this instance's own section wrapper) - same reason
        // as above, otherwise this always measures whichever header happens to be
        // first in the DOM, hidden or not.
        const header = this.header.querySelector('.header__logo-wrapperSmall');
        if (!header) return;
        document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);

        // Set big logo height once
        const largeLogo = this.header.querySelector('.Header__Logo--large');
        if (largeLogo) {
          document.documentElement.style.setProperty('--logo-height', `${largeLogo.offsetHeight}px`);
        }
      }


      disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
        if (this.revealReadyObserver) this.revealReadyObserver.disconnect();
        if (this._menuDrawerDetails && this._menuDrawerToggle) {
          this._menuDrawerDetails.removeEventListener('toggle', this._menuDrawerToggle);
        }
      }

      createObserver() {
        let observer = new IntersectionObserver((entries, observer) => {
          this.headerBounds = entries[0].intersectionRect;
          observer.disconnect();
        });

        observer.observe(this.header);
      }

      updateHeaderVisibilityClass(scrollTop) {
        const announcementBar = document.querySelector('.utility-bar');
        const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
        const headerHeight = this.header ? this.header.offsetHeight : 0;
        const isBigLogoVersion = this.classList.contains('transprent-header-big');

        // Get logo height from CSS custom property
        const logoHeight = isBigLogoVersion
          ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--logo-height')) || 0
          : 0;

        // For big logo version, include the big logo height in threshold
        const threshold = isBigLogoVersion
          ? headerHeight + announcementHeight + logoHeight
          : headerHeight + announcementHeight;

        // Only add/remove header-strictly-hidden class on collection, page, blog, and article pages
        if (this.isCollectionPage || this.isContentPage) {
          if (scrollTop > threshold) {
            document.body.classList.add('header-strictly-hidden');
          } else if (scrollTop <= announcementHeight && scrollTop < this.lastScrollTop) {
            // Only remove class when scrolling up (reverse) and within announcement bar height
            document.body.classList.remove('header-strictly-hidden');
          }
        }

        this.lastScrollTop = scrollTop;
      }

      syncAnnouncementPastClass(scrollTop) {
        const announcementBar = document.querySelector('.utility-bar');
        const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
        document.body.classList.toggle('announcement-scrolled-past', scrollTop >= announcementHeight);
      }

      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        this.syncAnnouncementPastClass(scrollTop);

        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
        // Short-circuit all DOM operations while parallax animation owns the header.
        // Every window.scrollTo during the 720ms snap fires this handler, triggering
        // syncGalleryToHeader (adds gallery-animate class → 550ms media-wrapper transition)
        // and DOM class operations — all causing layout/repaint during animation = flicker.
        if (this.preventHide) return;

        const isTransparentHeader = this.classList.contains('transprent-header') || this.classList.contains('transprent-header-big');
        const isScrollingDown = scrollTop > this.currentScrollTop;
        const isScrollingUp = scrollTop < this.currentScrollTop;

        const headerBottom = this.headerBounds.bottom || 0;
        if (isScrollingDown && scrollTop > headerBottom) {
          this.header.classList.add('scrolled-past-header');
          // For transparent headers, don't add solid-header-transition when scrolling down
          if (isTransparentHeader) {
            this.header.classList.remove('solid-header-transition');
          }
          if (this.preventHide) return;
          requestAnimationFrame(this.hide.bind(this));
        } else if (isScrollingUp && scrollTop > headerBottom + 20) {
          this.header.classList.add('scrolled-past-header');
          // For transparent headers, add solid-header-transition when scrolling up
          if (isTransparentHeader) {
            if (scrollTop > 150) {
              this.header.classList.add('solid-header-transition');
            } else {
              this.header.classList.remove('solid-header-transition');
            }
          }
          if (!this.preventReveal) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);

            if (!this.preventHide) requestAnimationFrame(this.hide.bind(this));
          }
        } else if (scrollTop <= (this.headerBounds.top || 0)) {
          this.header.classList.remove('scrolled-past-header');
          this.header.classList.remove('solid-header-transition');
          requestAnimationFrame(this.reset.bind(this));
        }

        if (isTransparentHeader && scrollTop <= 150) {
          this.header.classList.remove('solid-header-transition');
        }

        this.updateHeaderVisibilityClass(scrollTop);
        this.currentScrollTop = scrollTop;
      }

      syncGalleryToHeader() {
        if (this._galleryAnimTimer) clearTimeout(this._galleryAnimTimer);
        const parallaxRoot = document.querySelector('.product-paralax');
        if (!parallaxRoot) return;
        // Skip gallery-animate during drag/snap — transitions are suppressed and
        // adding this class would race with is-dragging/is-snapping suppression.
        if (parallaxRoot.classList.contains('is-dragging') || parallaxRoot.classList.contains('is-snapping')) return;
        parallaxRoot.classList.add('gallery-animate');
        this._galleryAnimTimer = setTimeout(() => {
          parallaxRoot.classList.remove('gallery-animate');
          this._galleryAnimTimer = null;
        }, 550);
      }

      hide() {

        if (this.headerIsAlwaysSticky) return;
        if (this.preventHide) return;

        // Transparent header: skip hide on desktop only.
        // On mobile, allow hide/reveal after parallax animation completes.
        const isTransparentHeader = this.classList.contains('transprent-header') || this.classList.contains('transprent-header-big');
        if (isTransparentHeader && window.innerWidth > 1023) return;

        const details = this.querySelectorAll("header-megamenu details");
        let hasDetailOpen = false;

        details.forEach(detail => {
          if (detail.hasAttribute("open")) {
            hasDetailOpen = true;
          }
        });

        // if(hasDetailOpen == true && window.pageYOffset > 200) return

        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
        this.header.classList.remove('solid-header-transition');
        this.syncGalleryToHeader();
        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
        this.syncGalleryToHeader();
        const isTransparentHeader = this.classList.contains('transprent-header') || this.classList.contains('transprent-header-big');
        // For transparent headers, always add solid-header-transition on reveal (reverse scroll)
        if (isTransparentHeader) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          if (scrollTop > 150) {
            this.header.classList.add('solid-header-transition');
          } else {
            this.header.classList.remove('solid-header-transition');
          }
        }
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;
        // Don't reset while parallax animation owns the header — prevents scroll-to-0
        // during last-slide reveal from stripping shopify-section-header-sticky mid-animation.
        if (this.preventHide) return;
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      }

      closeMenuDisclosure() {
        this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
        this.disclosures.forEach((disclosure) => disclosure.close());
      }

      closeSearchModal() {
        this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      }
    }

    customElements.define('sticky-header', StickyHeader);
  }


  // Big Logo JS
  const headerLogo = document.querySelector('.Header__Logo--large');
  const smallHeaderLogo = document.querySelector('.Header__Logo--small.show-with-big-logo');
  const headerWrapper = document.querySelector('.header-wrapper');

  let logoScrolledTimer = null;

  // Function to handle scroll events
  function handleScroll() {
    if (!headerLogo) return;
    // Skip logo collapse animation while the parallax panel drag/snap owns the scroll —
    // programmatic scrollTo() calls during drag fire this handler and would trigger the
    // animate-logo shrink, making the header visually "close" mid-gesture.
    if (typeof window._pdpParallaxIsBusy === 'function' && window._pdpParallaxIsBusy()) return;
    if (window.scrollY > 10) {
      // Only kick off the timer if the animation isn't already running
      if (!headerLogo.classList.contains('animate-logo')) {
        clearTimeout(logoScrolledTimer);
        headerLogo.classList.add('animate-logo');
        if (smallHeaderLogo) smallHeaderLogo.classList.add('hide-logo');
        // Add class exactly when the 0.8s CSS animation finishes
        logoScrolledTimer = setTimeout(() => {
          if (headerLogo.classList.contains('animate-logo')) {
            headerWrapper.classList.add('logo-scrolled');
          }
        }, 700);
      }
    } else {
      // Reverse: remove instantly the moment animation starts going back
      clearTimeout(logoScrolledTimer);
      headerWrapper.classList.remove('logo-scrolled');
      headerLogo.classList.remove('animate-logo');
      if (smallHeaderLogo) smallHeaderLogo.classList.remove('hide-logo');
    }
  }

  window.addEventListener('scroll', handleScroll);
  window._pdpSyncBigLogo = handleScroll;
  handleScroll();
})();
